import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// ── Real-time status from timestamps ──────────────────────
const getRealStatus = (auction) => {
  const now   = new Date();
  const start = new Date(auction.start_time);
  const end   = new Date(auction.end_time);
  if (auction.status === 'cancelled') return 'cancelled';
  if (now < start)              return 'upcoming';
  if (now >= start && now <= end) return 'live';
  return 'ended';
};

// ── Countdown Timer ───────────────────────────────────────
const CountdownTimer = ({ endTime }) => {
  const calc = useCallback(() => {
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, over: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
      over: false,
    };
  }, [endTime]);

  const [time, setTime] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [calc]);

  if (time.over) return <span className="text-red-400 font-bold text-xs uppercase tracking-widest">Ended</span>;

  return (
    <div className="flex items-center gap-1">
      {time.d > 0 && <span className="text-xs font-black text-white">{time.d}d </span>}
      <span className="text-xs font-black text-white">{String(time.h).padStart(2, '0')}</span>
      <span className="text-primary text-xs font-black">:</span>
      <span className="text-xs font-black text-white">{String(time.m).padStart(2, '0')}</span>
      <span className="text-primary text-xs font-black">:</span>
      <span className="text-xs font-black text-white">{String(time.s).padStart(2, '0')}</span>
    </div>
  );
};

// ── Status Badge ──────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    live:      { bg: 'bg-red-500',   text: 'LIVE',      dot: true  },
    upcoming:  { bg: 'bg-amber-400', text: 'UPCOMING',  dot: false },
    ended:     { bg: 'bg-slate-400', text: 'ENDED',     dot: false },
    cancelled: { bg: 'bg-slate-300', text: 'CANCELLED', dot: false },
  };
  const c = config[status] || config.ended;
  return (
    <span className={`${c.bg} text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5`}>
      {c.dot && <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block" />}
      {c.text}
    </span>
  );
};

// ── Main Component ────────────────────────────────────────
const AuctionsList = () => {
  const navigate = useNavigate();
  const [auctions,     setAuctions]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');
  const [registrationMap, setRegistrationMap] = useState({});
  const { user } = useAuth();

  useEffect(() => {
    const fetchAuctions = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter) params.append('status', statusFilter);
        const res = await api.get(`/api/auctions?${params}`);
        setAuctions(res.data);
        // Fetch registration status for live auctions (only if logged in)
        if (user?.id) {
          const liveAuctions = res.data.filter(a => getRealStatus(a) === 'live' || a.status === 'live');
          const statusMap = {};
          await Promise.all(liveAuctions.map(async (a) => {
            try {
              const r = await api.get(`/api/auctions/${a.public_id || a.id}/register-status`);
              statusMap[a.id] = r.data.registered;
            } catch {
              statusMap[a.id] = false;
            }
          }));
          setRegistrationMap(statusMap);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAuctions();
  }, [statusFilter, user?.id]);

  const filtered = auctions.filter(a => {
    const realStatus = getRealStatus(a);
    const matchSearch = !searchTerm ||
      a.animal_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.breed?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.seller_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = !statusFilter || realStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  const liveCount     = auctions.filter(a => getRealStatus(a) === 'live').length;
  const upcomingCount = auctions.filter(a => getRealStatus(a) === 'upcoming').length;

  return (
    <div className="min-h-screen bg-background text-slate-900 font-body antialiased">
      <Navbar variant="default" />

      <main className="pt-20">

        {/* Hero */}
        <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src="/assets/auction-hero.jpg" alt="Auctions"
              className="w-full h-full object-cover" />
            <div className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0.1) 100%)' }} />
          </div>
          <div className="relative z-10 w-full max-w-screen-2xl mx-auto px-6 lg:px-12 pb-16">
            <div className="flex items-center gap-3 mb-4">
              {liveCount > 0 && (
                <span className="bg-red-500 text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse inline-block" />
                  {liveCount} Live Now
                </span>
              )}
              <span className="text-emerald-400 font-bold tracking-[0.4em] uppercase text-xs">
                {upcomingCount} Upcoming
              </span>
            </div>
            <h1 className="text-6xl lg:text-8xl font-headline font-extrabold text-white tracking-tight leading-none">
              Live <span style={{ color: '#06A788' }}>Auctions</span>
            </h1>
            <p className="text-white/70 text-lg mt-4 max-w-xl font-light">
              Bid on premium livestock in real-time. Verified animals, transparent pricing, secure transactions.
            </p>
          </div>
        </section>

        {/* Filter Bar */}
        <section className="bg-white border-b border-slate-100 sticky top-20 z-40 shadow-sm">
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-4 flex flex-wrap gap-4 items-center">
            <div className="flex items-center bg-slate-50 border border-slate-200 px-4 py-2.5 rounded-full flex-1 max-w-sm gap-3">
              <span className="material-symbols-outlined text-slate-400 text-sm">search</span>
              <input
                type="text"
                placeholder="Search animal, breed, seller..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-none text-sm text-slate-800 placeholder:text-slate-400 focus:ring-0 outline-none w-full"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="text-slate-400 hover:text-primary">
                  <span className="material-symbols-outlined text-sm">close</span>
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {[
                { value: '',         label: 'All'          },
                { value: 'live',     label: '🔴 Live'      },
                { value: 'upcoming', label: '🟡 Upcoming'  },
                { value: 'ended',    label: 'Ended'        },
              ].map(tab => (
                <button key={tab.value} onClick={() => setStatusFilter(tab.value)}
                  className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                    statusFilter === tab.value
                      ? 'bg-primary text-white border-primary'
                      : 'border-slate-200 text-slate-600 hover:border-primary/50'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            <p className="text-sm text-slate-500 font-medium ml-auto">
              {loading ? 'Loading...' : `${filtered.length} auctions`}
            </p>
          </div>
        </section>

        {/* Auctions Grid */}
        <section className="py-20 max-w-screen-2xl mx-auto px-6 lg:px-12">
          {error && <div className="text-center py-20 text-red-500 font-semibold">{error}</div>}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse border border-slate-100">
                  <div className="h-64 bg-slate-100" />
                  <div className="p-8 space-y-4">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-100 rounded w-1/2" />
                    <div className="h-10 bg-slate-100 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-32">
              <span className="material-symbols-outlined text-6xl text-slate-300 block mb-4">gavel</span>
              <p className="text-2xl font-bold text-slate-400">No auctions found</p>
              <p className="text-slate-400 mt-2">Check back soon for upcoming auctions</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {filtered.map((auction) => {
                const realStatus = getRealStatus(auction);
                return (
                  <div
                    key={auction.id}
                    onClick={() => navigate(`/auction/${auction.public_id || auction.id}`)}
                    className="group bg-white rounded-3xl overflow-hidden border border-slate-100 cursor-pointer transition-all duration-500 hover:-translate-y-2"
                    style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.08)' }}
                  >
                    {/* Image */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={auction.image_url || '/assets/auction-hero.jpg'}
                        alt={auction.animal_name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <StatusBadge status={realStatus} />
                      </div>
                      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-primary">
                        {auction.category}
                      </div>
                      {(realStatus === 'live' || realStatus === 'upcoming') && (
                        <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary text-sm">timer</span>
                          <CountdownTimer endTime={realStatus === 'upcoming' ? auction.start_time : auction.end_time} />
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="p-8 space-y-5">
                      <div>
                        <h3 className="text-xl font-headline font-bold text-slate-900">
                          {auction.title || auction.animal_name}
                        </h3>
                        <p className="text-sm text-slate-400 mt-1">
                          {auction.breed} • {auction.age_months} Months • {auction.weight_kg} kg
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4 py-4 border-y border-slate-100">
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                            {auction.current_bid ? 'Current Bid' : 'Start Price'}
                          </p>
                          <p className="text-2xl font-headline font-black text-primary">
                            ₹{Number(auction.current_bid || auction.start_price).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Bids</p>
                          <p className="text-2xl font-headline font-black text-slate-900">{auction.total_bids}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-primary text-sm">storefront</span>
                          <span className="text-xs font-bold text-slate-500">{auction.seller_name}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <span className="material-symbols-outlined text-sm">location_on</span>
                          {auction.city}{auction.state ? `, ${auction.state}` : ''}
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/auction/${auction.public_id || auction.id}`);
                        }}
                        className={`w-full py-4 rounded-full font-bold text-sm tracking-wide transition-all ${
                          realStatus === 'live' && registrationMap[auction.id]
                            ? 'bg-primary text-white hover:bg-emerald-700 shadow-lg shadow-primary/20'
                            : realStatus === 'live' && !registrationMap[auction.id]
                            ? 'bg-amber-500 text-white hover:bg-amber-600'
                            : realStatus === 'upcoming'
                            ? 'bg-amber-400 text-white hover:bg-amber-500'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        {realStatus === 'live' && registrationMap[auction.id]
                          ? '🔴 Place Bid'
                          : realStatus === 'live' && !registrationMap[auction.id]
                          ? '💰 Register (₹25,000 deposit)'
                          : realStatus === 'upcoming'
                          ? '🟡 Starts In'
                          : 'Auction Ended'
                        }
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <Footer variant="default" />
    </div>
  );
};

export default AuctionsList;